import { memo, useCallback, useRef } from 'react';
import type { FC } from 'react';

import { useTranslation } from 'react-i18next';
import type { BaseSplashComponent } from '@vgtv/api-client/lib/component/splash';

import { Button } from '../../../Button/Button';
import { useSplashTracking } from '../../common/useSplashTracking';
import { useViewObserver } from '../../../utils/useViewObserver';
import { Splash } from '../../../Splash/Splash/Splash';

interface Props {
  componentConfig: BaseSplashComponent;
  isFirstComponent: boolean;
  pageComponentIndex: number;
}
const noop = () => {};

const CustomSplashBase: FC<Props> = (props) => {
  const { componentConfig, isFirstComponent, pageComponentIndex } = props;
  const { t } = useTranslation();
  const { overrides = {}, options = {} } = componentConfig;
  const ref = useRef<HTMLDivElement>(null!);

  const { image: imageUrl, title, description, url } = overrides;

  const isPulledUp = isFirstComponent && imageUrl !== undefined;
  const displaySplashButtons = Boolean(url);

  const { trackSplashView, trackCustomSplashClick } = useSplashTracking({
    elementType: 'CustomSplash',
    pageComponentIndex,
  });

  useViewObserver({
    onView: displaySplashButtons ? trackSplashView : noop,
    elementRef: ref,
  });

  const trackSplashClick = useCallback(() => {
    if (title && url) {
      trackCustomSplashClick({ title, url });
    }
  }, [trackCustomSplashClick, title, url]);

  if (!imageUrl && !title && !description && !url) {
    return null;
  }

  const buttons = Boolean(url) && (
    <Button
      component="a"
      size="large"
      variant="contained"
      active
      href={url}
      onClick={trackSplashClick}
    >
      {options.buttonLabel || t('Click to see more')}
    </Button>
  );

  return (
    <Splash
      imageUrl={imageUrl}
      title={title!}
      description={description}
      buttons={buttons}
      lazy={isFirstComponent}
      ref={ref}
      isPulledUp={isPulledUp}
    />
  );
};

export const CustomSplash = memo(CustomSplashBase);
